/**
 * Lightweight Dialog (RLY-024).
 *
 * Documented AC variance per [06]: the dispatch brief mentions shadcn
 * `<Sheet>` or `<Dialog>` for the New-Agent flow. We chose `<Dialog>` here
 * (centered modal) over `<Sheet>` (slide-in side panel) and ship a plain
 * focus-trap-free implementation rather than Radix's full primitive — Phase-1
 * polish bar accepts the deviation; arrow-key/focus-trap polish lands in a
 * Sprint 07+ a11y story.
 *
 * Behavior:
 *   - `open` controls visibility; `onOpenChange(false)` is fired on
 *     overlay-click, Escape, or the close button.
 *   - The dialog role is `dialog` with `aria-modal="true"`.
 *   - The first focusable child is auto-focused on open.
 */
import * as React from "react";

import { cn } from "@/lib/utils";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Auto-focus the first focusable element when the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    const root = containerRef.current;
    if (!root) return;
    const focusable = root.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [open]);

  // Escape closes.
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;
  return (
    <div
      ref={containerRef}
      data-testid="dialog-root"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/50"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-10 w-full max-w-lg overflow-y-auto rounded-lg border bg-background p-6 shadow-lg sm:max-h-[90vh]"
      >
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5 text-left", className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}
